import {
    useEffect,
    useState
} from "react";
import {
    type ScaledSize,
    Dimensions,
    Platform
} from "react-native";
import {
    NCoreUIKitToast
} from "ncore-ui-kit";
import RNFetchBlob from "react-native-blob-util";
import {
    SERVER_ADDRESS_STORAGE
} from "../services/restAPI/constants";

export const useDebouncedDimensions = (delay: number = 200) => {
    const getWidth = (w: number) => {
        if (Platform.OS === "web") {
            return Math.min(w, 480);
        }
        return w;
    };

    const [
        dimensions,
        setDimensions
    ] = useState({
        windowWidth: getWidth(Dimensions.get("window").width),
        windowHeight: Dimensions.get("window").height,
        actualWindowWidth: Dimensions.get("window").width,
        actualWindowHeight: Dimensions.get("window").height
    });

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;

        const subscription = Dimensions.addEventListener("change", ({
            window
        }: { window: ScaledSize }) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                setDimensions({
                    windowWidth: getWidth(window.width),
                    windowHeight: window.height,
                    actualWindowWidth: window.width,
                    actualWindowHeight: window.height
                });
            }, delay);
        });

        return () => {
            clearTimeout(timeoutId);
            subscription?.remove();
        };
    }, [
        delay
    ]);

    return dimensions;
};

export const getFirstAndLastWord = (text: string) => {
    const parts = text.trim().split(/\s+/);
    if (parts.length === 1) {
        return {
            first: parts[0],
            last: ""
        };
    };
    return {
        first: parts[0],
        last: parts[parts.length - 1]
    };
};

export const phoneNumberMask = (number: string) => {
    const digits = number.replace(/\D/g, "");

    let masked = "";

    if (digits.length === 0) {
        return "";
    }

    masked += "(";

    if (digits.length <= 3) {
        masked += digits;
    } else {
        masked += digits.slice(0, 3) + ") ";
        if (digits.length <= 6) {
            masked += digits.slice(3);
        } else {
            masked += digits.slice(3, 6) + "-";
            masked += digits.slice(6, 10);
        }
    }

    return masked;
};

export const extractDigits = (text: string) => {
    return text.replace(/\D/g, "");
};

export const formatDate = (dateString: string): string => {
    if (!dateString) return "";

    const date = new Date(dateString);

    return new Intl.DateTimeFormat("tr-TR", {
        day: "numeric",
        month: "long"
    }).format(date);
};

export const passwordValidation = (text: string) => {
    const regex = /^(?=.*[a-zçğıöşü])(?=.*[A-ZÇĞİÖŞÜ])(?=.*\d)(?=.*[^a-zA-ZçğıöşüÇĞİÖŞÜ0-9\s]).{8,}$/;
    return regex.test(text);
};

export const handleUpload = async (selectedFile: { uri: string; name?: string; type?: string; size?: number; }, uploadToken: string, _docID?: string, _maxFileSize?: number) => {

    if (!selectedFile || !uploadToken) {
        NCoreUIKitToast.open({
            title: "no-file-selected-or-upload-token-missing",
            isShowAction: false,
            type: "warning"
        });
        return;
    }
    const maxFileSize = _maxFileSize || 1024 * 1024;

    if (selectedFile.size && selectedFile.size > maxFileSize) {
        NCoreUIKitToast.open({
            title: "file-size-exceeds-limit",
            isShowAction: false,
            type: "warning"
        });
        return;
    }
    if (Platform.OS === "web") {
        const response = await fetch(selectedFile.uri);
        const blob = await response.blob();

        const formData = new FormData();
        formData.append("file", blob, selectedFile.name || "image.jpg");

        const uploadRes = await fetch(`${SERVER_ADDRESS_STORAGE}/upload`, {
            method: "POST",
            headers: {
                "Authorization": uploadToken,
                "file-type": selectedFile.type || "application/octet-stream"
            },
            body: formData
        });

        if (![
            200,
            201,
            202
        ].includes(uploadRes.status)) {
            throw new Error("Upload failed: " + uploadRes.status);
        }
        return;
    }

    selectedFile.uri = selectedFile.uri.replace("file://", "");
    try {
        await RNFetchBlob.config({
            timeout: 10 * 60 * 1000
        })
        /*  .fetch(
                "POST",
                `${SERVER_ADDRESS_STORAGE}/upload`,
                {
                           "content-length": selectedFile.size ? selectedFile.size.toString() : "0",
                    "Content-Type": "multipart/form-data",
                    "file-type": selectedFile.type,
                    "Authorization": uploadToken
                },
                RNFetchBlob.wrap(selectedFile.uri)
            ) */
            .fetch(
                "POST",
                `${SERVER_ADDRESS_STORAGE}/upload`,
                {
                    "Content-Type": "multipart/form-data",
                    "file-type": selectedFile.type || "application/octet-stream",
                    "Authorization": uploadToken
                },
                [
                    {
                        name: "file",
                        filename: selectedFile.name || "image.jpg",
                        type: selectedFile.type || "application/octet-stream",
                        data: RNFetchBlob.wrap(selectedFile.uri)
                    }
                ]
            )
            .uploadProgress({
                interval: 250
            }, () => {
            }).then(res => {
                const statusCode = res.info().status;
                if(!([
                    200,
                    201,
                    202
                ].includes(statusCode))){
                    throw res;
                }
            })
            .catch((err) => {
                throw err;
            });
    } catch (_err) {
        const err = _err as unknown as { message: string };
        if (err.message){
            throw err;
        }
        else{
            err.message = "undefined-error-message";
            throw err;
        }
    }
};

export const useWebScrollDrag = (ref: any /* eslint-disable-line */) => {
    useEffect(() => {
        if (Platform.OS !== "web") return;

        let timeoutId: NodeJS.Timeout;
        const init = () => {
            if (!ref || !ref.current) {
                timeoutId = setTimeout(init, 200);
                return;
            }
            const node = ref.current;

            const getDomNode = () => {
                if (node.getScrollableNode) return node.getScrollableNode();
                if (node._carouselRef && node._carouselRef.getScrollableNode) return node._carouselRef.getScrollableNode();
                if (node._scrollView && node._scrollView.getScrollableNode) return node._scrollView.getScrollableNode();
                return (node as any /* eslint-disable-line */);
            };

            const domNode = getDomNode();

            if (!domNode || !domNode.addEventListener) {
                timeoutId = setTimeout(init, 200);
                return;
            }

            let isDown = false;
            let startX: number;
            let scrollLeft: number;

            const onMouseDown = (e: MouseEvent) => {
                isDown = true;
                domNode.style.cursor = "grabbing";
                startX = e.pageX - domNode.offsetLeft;
                scrollLeft = domNode.scrollLeft;
            };
            const onMouseLeave = () => {
                isDown = false;
                domNode.style.cursor = "grab";
            };
            const onMouseUp = () => {
                isDown = false;
                domNode.style.cursor = "grab";
            };
            const onMouseMove = (e: MouseEvent) => {
                if (!isDown) return;
                e.preventDefault();
                const x = e.pageX - domNode.offsetLeft;
                const walk = (x - startX) * 2;
                domNode.scrollLeft = scrollLeft - walk;
            };

            domNode.addEventListener("mousedown", onMouseDown);
            domNode.addEventListener("mouseleave", onMouseLeave);
            domNode.addEventListener("mouseup", onMouseUp);
            domNode.addEventListener("mousemove", onMouseMove);

            domNode.style.cursor = "grab";

            domNode._cleanupWebDrag = () => {
                domNode.removeEventListener("mousedown", onMouseDown);
                domNode.removeEventListener("mouseleave", onMouseLeave);
                domNode.removeEventListener("mouseup", onMouseUp);
                domNode.removeEventListener("mousemove", onMouseMove);
            };
        };
        init();

        return () => {
            clearTimeout(timeoutId);
            if (ref && ref.current) {
                const node = ref.current;
                const getDomNode = () => {
                    if (node.getScrollableNode) return node.getScrollableNode();
                    if (node._carouselRef && node._carouselRef.getScrollableNode) return node._carouselRef.getScrollableNode();
                    if (node._scrollView && node._scrollView.getScrollableNode) return node._scrollView.getScrollableNode();
                    return (node as any /* eslint-disable-line */);
                };
                const domNode = getDomNode();
                if (domNode && domNode._cleanupWebDrag) {
                    domNode._cleanupWebDrag();
                }
            }
        };
    }, []);
};
